import React from "react";
import { Home, RefreshCw } from "lucide-react";
import ReportErrorButton from "./ReportErrorButton";

// Branded crash screen. Catches render/runtime errors in the app tree so a JS error
// shows an actionable page (with Report Error) instead of a blank white screen.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "", stack: "" };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      message: (error && error.message) || "Unexpected error",
      stack: (error && error.stack) || "",
    };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("App crashed:", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen bg-[#0D0D0D] text-white flex items-center justify-center px-6" data-testid="app-error-boundary">
        <div className="max-w-[560px] w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#FF5C00]/12 border border-[#FF5C00]/30 mb-8">
            <RefreshCw size={30} className="text-[#FF7A2F]" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold">Something went wrong</h1>
          <p className="text-neutral-400 text-base mt-4 max-w-[440px] mx-auto">
            An unexpected error interrupted this page. You can reload to try again, or let us
            know so we can fix it fast.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-9">
            <button
              onClick={() => { window.location.href = "/"; }}
              data-testid="error-home-btn"
              style={{ backgroundColor: "#1A8A4A" }}
              className="inline-flex items-center gap-2 text-white font-semibold px-7 py-3.5 rounded-xl hover:brightness-110 transition-all"
            >
              <Home size={18} /> Back to home
            </button>
            <button
              onClick={() => window.location.reload()}
              data-testid="error-reload-btn"
              className="inline-flex items-center gap-2 text-neutral-200 font-semibold px-7 py-3.5 rounded-xl border border-[#2A2A2A] hover:border-[#3A3A3A] bg-[#181818] transition-colors"
            >
              <RefreshCw size={16} /> Reload page
            </button>
            <ReportErrorButton
              context="app-crash"
              message={this.state.message}
              stack={this.state.stack}
              testId="error-report-btn"
            />
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
